<template>
    <div class="field">
        <div class="control">
            <label v-if="label" class="label">{{ label }}</label>
            <div v-if="loading" class="has-text-centered p-2">
                <span class="icon">
                    <font-awesome-icon :icon="['fas', 'spinner']" spin />
                </span>
                <span>{{ loadingText }}</span>
            </div>
            <div v-else>
                <div class="dropdown is-fullwidth" :class="{'is-active': isDropdownActive}">
                    <div class="dropdown-trigger" style="width: 100%;">
                        <div class="field">
                            <div class="control has-icons-right">
                                <input 
                                    class="input" 
                                    type="text" 
                                    :placeholder="placeholder" 
                                    :disabled="disabled"
                                    v-model="searchValue" 
                                    @focus="isDropdownActive = true"
                                    @blur="handleBlur">
                                <span class="icon is-small is-right">
                                    <font-awesome-icon :icon="['fas', 'angle-down']" />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="dropdown-menu" style="width: 100%;">
                        <div class="dropdown-content">
                            <a v-for="item in filteredItems" 
                               :key="item.id" 
                               class="dropdown-item"
                               @click="selectItem(item)">
                                <slot name="item" :item="item">
                                    {{ getDisplayText(item) }}
                                </slot>
                            </a>
                            <div v-if="filteredItems.length === 0" class="dropdown-item">
                                {{ noResultsText }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="validationError && touched" class="notification is-danger is-light">
        {{ validationError }}
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
    name: 'SearchDropdown',
    props: {
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Search...'
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingText: {
            type: String,
            default: 'Loading...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: () => []
        },
        modelValue: {
            type: String,
            default: ''
        },
        searchText: {
            type: String,
            default: ''
        },
        validationError: {
            type: String,
            default: ''
        },
        touched: {
            type: Boolean,
            default: false
        },
        noResultsText: {
            type: String,
            default: 'No matching results found'
        },
        searchFields: {
            type: Array,
            default: () => ['value'] // Default field to search in
        },
        displayField: {
            type: String,
            default: 'value' // Default field to display
        }
    },
    emits: ['update:modelValue', 'update:searchText', 'select', 'blur'],
    setup(props, { emit }) {
        const isDropdownActive = ref(false);
        const searchValue = ref(props.searchText);

        // Watch for external changes to searchText prop
        watch(() => props.searchText, (newValue) => {
            searchValue.value = newValue;
        });

        // Watch for changes to searchValue and emit updates
        watch(searchValue, (newValue) => {
            emit('update:searchText', newValue);
        });

        const filteredItems = computed(() => {
            if (!searchValue.value) return props.items;
            const search = searchValue.value.toLowerCase();
            return props.items.filter(item => {
                return props.searchFields.some(field => {
                    const fieldValue = getNestedValue(item, field);
                    return fieldValue && fieldValue.toString().toLowerCase().includes(search);
                });
            });
        });

        const getNestedValue = (obj, path) => {
            return path.split('.').reduce((current, key) => current?.[key], obj);
        };

        const getDisplayText = (item) => {
            return getNestedValue(item, props.displayField) || '';
        };

        const selectItem = (item) => {
            emit('update:modelValue', item.id);
            searchValue.value = getDisplayText(item);
            isDropdownActive.value = false;
            emit('select', item);
        };

        const handleBlur = () => {
            setTimeout(() => {
                isDropdownActive.value = false;
                emit('blur');
            }, 200);
        };

        return {
            isDropdownActive,
            searchValue,
            filteredItems,
            selectItem,
            handleBlur,
            getDisplayText
        };
    }
};
</script>

<style scoped>
.dropdown {
    width: 100%;
}
</style>